/**
 * API service for BCR (Basic Software Change Request) management.
 */

import apiClient from './client';
import type {
  BcrTaskCreate,
  BcrTaskDetail,
  BcrTaskListItem,
  BcrStatusBrief,
  BcrStatsResponse,
  McuGitInfo,
  PaginatedResponse,
} from '@/types';

export const listBcrTasks = async (
  projectId: number,
  params?: { package_id?: number; status?: string; page?: number; page_size?: number },
): Promise<PaginatedResponse<BcrTaskListItem>> => {
  const response = await apiClient.get<PaginatedResponse<BcrTaskListItem>>(
    `/bcr/${projectId}/tasks/`,
    { params },
  );
  return response.data;
};

export const createBcrTask = async (
  projectId: number,
  data: BcrTaskCreate,
): Promise<BcrTaskDetail> => {
  const response = await apiClient.post<BcrTaskDetail>(
    `/bcr/${projectId}/tasks/`,
    data,
  );
  return response.data;
};

export const getBcrTaskDetail = async (
  projectId: number,
  taskId: number,
  logOffset?: number,
): Promise<BcrTaskDetail> => {
  const params = logOffset !== undefined ? { log_offset: logOffset } : undefined;
  const response = await apiClient.get<BcrTaskDetail>(
    `/bcr/${projectId}/tasks/${taskId}/`,
    { params },
  );
  return response.data;
};

export const updateBcrTaskCustomArtifacts = async (
  projectId: number,
  taskId: number,
  customArtifacts: Array<{ kind: string; name: string; url?: string }>,
): Promise<BcrTaskDetail> => {
  const response = await apiClient.patch<BcrTaskDetail>(
    `/bcr/${projectId}/tasks/${taskId}/`,
    { custom_artifacts: customArtifacts },
  );
  return response.data;
};

export const updatePackageCustomArtifacts = async (
  packageId: number,
  customArtifacts: Array<{ kind: string; name: string; url?: string }>,
): Promise<any> => {
  const response = await apiClient.patch<any>(
    `/packages/${packageId}/custom-artifacts/`,
    { custom_artifacts: customArtifacts },
  );
  return response.data;
};

export const cancelBcrTask = async (
  projectId: number,
  taskId: number,
): Promise<BcrTaskDetail> => {
  const response = await apiClient.post<BcrTaskDetail>(
    `/bcr/${projectId}/tasks/${taskId}/cancel/`,
  );
  return response.data;
};

export const rerunBcrSubTask = async (
  projectId: number,
  taskId: number,
  subTaskName: string,
): Promise<BcrTaskDetail> => {
  const response = await apiClient.post<BcrTaskDetail>(
    `/bcr/${projectId}/tasks/${taskId}/`,
    { action: 'rerun', sub_task_name: subTaskName },
  );
  return response.data;
};

export const deleteBcrTask = async (
  projectId: number,
  taskId: number,
): Promise<void> => {
  await apiClient.delete(`/bcr/${projectId}/tasks/${taskId}/`);
};

export const getPackageLatestBcr = async (
  packageId: number,
): Promise<BcrStatusBrief> => {
  const response = await apiClient.get<BcrStatusBrief>(
    `/bcr/package/${packageId}/latest/`,
  );
  return response.data;
};

export interface PackageMcuGitResponse {
  mcu_git: McuGitInfo | null;
}

export const getPackageMcuGit = async (
  packageId: number,
): Promise<PackageMcuGitResponse> => {
  const response = await apiClient.get<PackageMcuGitResponse>(
    `/bcr/package/${packageId}/mcu-git/`,
  );
  return response.data;
};

export interface RpaRequirementResult {
  rpa_requirement_id: string;
  rpa_requirement_url: string;
  is_update: boolean;
  module_adaptation_requirements?: Array<{
    module_name: string;
    rpa_id: string;
    rpa_url: string;
  }>;
}

export const createRpaRequirement = async (
  projectId: number,
  taskId: number,
): Promise<RpaRequirementResult> => {
  const response = await apiClient.post<RpaRequirementResult>(
    `/bcr/${projectId}/tasks/${taskId}/create-rpa-requirement/`,
  );
  return response.data;
};

export const getBatchBcrStatus = async (
  packageIds: number[],
): Promise<Record<string, BcrStatusBrief>> => {
  const response = await apiClient.post<Record<string, BcrStatusBrief>>(
    `/bcr/packages/status/`,
    { package_ids: packageIds },
  );
  return response.data;
};

export const getBcrStats = async (params?: {
  project_id?: number;
  granularity?: 'week' | 'month';
  start_date?: string;
  end_date?: string;
}): Promise<BcrStatsResponse> => {
  const response = await apiClient.get<BcrStatsResponse>('/bcr/stats/', { params });
  return response.data;
};
